import { useCallback, useMemo } from 'react'

import type { ExtendedBid } from '@/features/bidding/types/bid'
import type { BidOfferPriceActions } from '@/features/bidding/types/bidOfferPriceFlow'
import type { GroupedBidParams } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import type { AggregatedBidWrapper } from '@/features/bidding/utils/model/mapActivationGroupBidsToAggregatedBids'
import { byActivationGroupBidTransformer } from '@/features/bidding/utils/price/bidSaveTransformers'
import { validateByActivationGroupFlow } from '@/features/bidding/utils/validation/bidPriceFlowValidation'

import { useBidOfferPriceSaveActions } from './useBidOfferPriceSaveActions'

type UseBidOfferPriceActionsByActivationGroupParams = {
  groupParams: GroupedBidParams
}

/**
 * Hook that provides validation and save actions for the by-activation-group price flow.
 *
 * Implements the unified BidOfferPriceActions interface with by-activation-group-flow-specific logic:
 * - Validation: Checks each activation group for overbidding, underbidding, missing chunks, and Entelios-specific rules
 * - Save: Updates ExtendedBids with new price chunks from aggregated bids and saves
 *
 * @param params - Configuration object containing groupParams
 * @returns Actions interface with validate, save functions and isSaving state
 */
export function useBidOfferPriceActions_ByActivationGroup(
  params: UseBidOfferPriceActionsByActivationGroupParams,
): BidOfferPriceActions<{ bidsPerActivationGroup: Map<string, AggregatedBidWrapper>; bidsWithPrices: ExtendedBid[] }> {
  const { groupParams } = params
  const { saveBidsAndNavigate, isSaving } = useBidOfferPriceSaveActions({ groupParams })

  const validate = useCallback(
    (bidData: { bidsPerActivationGroup: Map<string, AggregatedBidWrapper>; bidsWithPrices: ExtendedBid[] }) => {
      return validateByActivationGroupFlow(bidData.bidsPerActivationGroup, groupParams)
    },
    [],
  )

  const save = useCallback(
    async (bidData: { bidsPerActivationGroup: Map<string, AggregatedBidWrapper>; bidsWithPrices: ExtendedBid[] }) => {
      const extendedBids = await byActivationGroupBidTransformer(bidData)
      await saveBidsAndNavigate(extendedBids)
    },
    [saveBidsAndNavigate],
  )

  return useMemo(
    () => ({
      validate,
      save,
      isSaving,
    }),
    [validate, save, isSaving],
  )
}
